"use client"

import { forwardRef, useState, useEffect, useId, Children, isValidElement } from "react"
import type { ElementType, CSSProperties } from "react"
import { useSidebarContext } from "./hooks/useSidebarContext"
import { useSubContent } from "./hooks/useSubContent"
import type { SidebarItemProps, SidebarItemRenderProp } from "./Sidebar.types"
import {
  SIDEBAR_TAILWIND_CLASSES,
  SIDEBAR_DATA_ATTRIBUTES,
  SIDEBAR_ARIA_ROLES,
} from "./Sidebar.constants"
import { SidebarSubContent } from "./SidebarSubContent"

/**
 * Componente item de navegación del Sidebar
 *
 * @description
 * Item individual de navegación dentro del sidebar.
 * Renderiza un `<a>` por defecto, pero puede ser cualquier elemento
 * o componente (ej: React Router Link, Next.js Link).
 *
 * Características:
 * - Soporta iconos y labels
 * - Gestiona estados de hover y focus
 * - Se registra automáticamente para navegación por teclado
 * - Aplica data attributes para styling
 * - Soporta render props para control total
 * - Compatible con React Router, Next.js, etc. via prop `as`
 * - Label oculto automáticamente cuando sidebar está colapsado (via CSS container queries)
 *
 * @example
 * ```tsx
 * // Uso básico
 * <SidebarItem
 *   href="/"
 *   icon={<HomeIcon />}
 *   label="Home"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Con React Router
 * <SidebarItem
 *   as={Link}
 *   to="/dashboard"
 *   icon={<DashboardIcon />}
 *   label="Dashboard"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Con Next.js Link
 * <SidebarItem
 *   LinkComponent={Link}
 *   href="/profile"
 *   icon={<UserIcon />}
 *   label="Profile"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Con render props
 * <SidebarItem href="/settings">
 *   {({ focused, hovered, disabled }) => (
 *     <div className={cn(
 *       'flex items-center gap-2',
 *       focused && 'ring-2',
 *       hovered && 'bg-zinc-800',
 *       disabled && 'opacity-50'
 *     )}>
 *       <SettingsIcon />
 *       <span>Settings</span>
 *     </div>
 *   )}
 * </SidebarItem>
 * ```
 *
 * @example
 * ```tsx
 * // Con callbacks
 * <SidebarItem
 *   href="/analytics"
 *   icon={<ChartIcon />}
 *   label="Analytics"
 *   onHover={() => prefetchAnalytics()}
 *   onClick={() => trackNavigation('analytics')}
 * />
 * ```
 */
export const SidebarItem = forwardRef<HTMLAnchorElement, SidebarItemProps>(function SidebarItem(
  {
    href,
    icon,
    label,
    children,
    LinkComponent,
    linkProps = {},
    className = "",
    style,
    onHover,
    onClick,
    disabled = false,
    as,
    ...props
  },
  forwardedRef,
) {
  const { registerItem, unregisterItem } = useSidebarContext()
  const { registerSubContent, unregisterSubContent } = useSubContent()

  const [isHovered, setIsHovered] = useState(false)
  const [isFocused, setIsFocused] = useState(false)

  // Generar ID único para este item
  const itemId = useId()

  // Detectar SidebarSubContent en children
  let subContentElement: any = null
  let otherChildren: any = null

  if (children && typeof children !== 'function') {
    Children.forEach(children, (child) => {
      if (isValidElement(child) && child.type === SidebarSubContent) {
        subContentElement = child
      } else {
        otherChildren = child
      }
    })
  }

  // Determinar si children es render prop
  const isRenderProp = typeof children === "function"

  // Estado del item
  const itemState = {
    focused: isFocused,
    hovered: isHovered,
    disabled,
  }

  // Determinar el componente a renderizar
  // Prioridad: as > LinkComponent > "a"
  const Component = (as || LinkComponent || "a") as ElementType

  // Estilos del item (usando tokens CSS de shadcn)
  // El item crece para ocupar espacio disponible, el gap se maneja con padding
  // El hover ahora se aplica solo al icono, no al contenedor completo
  const itemStyles: CSSProperties = {
    paddingTop: "calc(var(--sidebar-items-gap) / 2)",
    paddingBottom: "calc(var(--sidebar-items-gap) / 2)",
    flex: "1 1 auto", // Grow para ocupar espacio disponible
    minHeight: "3rem", // Altura mínima
    ...style,
  }

  // Manejadores de eventos
  const handleMouseEnter = () => {
    setIsHovered(true)
    onHover?.()
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  const handleFocus = () => {
    setIsFocused(true)
    // CSS maneja el visual del icono elevado via data-focused
  }

  const handleBlur = () => {
    setIsFocused(false)
    // CSS maneja el visual del icono via data-focused
  }

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault()
      return
    }
    onClick?.()
  }

  // Registrar/des-registrar el item para navegación por teclado
  useEffect(() => {
    const element = forwardedRef && "current" in forwardedRef ? forwardedRef.current : null

    if (element) {
      registerItem(element)
      return () => unregisterItem(element)
    }
  }, [registerItem, unregisterItem, forwardedRef])

  // Registrar/des-registrar subContent si existe
  useEffect(() => {
    if (subContentElement) {
      const subContentData = {
        itemId,
        subContent: subContentElement.props.children,
        title: subContentElement.props.title,
      }
      registerSubContent(itemId, subContentData)
      return () => unregisterSubContent(itemId)
    }
  }, [itemId, subContentElement, registerSubContent, unregisterSubContent])

  // Props base del componente
  const baseProps = {
    ref: forwardedRef,
    className: `flex items-center justify-center w-full h-12 rounded-lg transition-colors focus-visible:outline-none ${className}`,
    style: itemStyles,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onClick: handleClick,
    role: SIDEBAR_ARIA_ROLES.listitem,
    "aria-label": label,
    title: label,
    tabIndex: disabled ? -1 : 0,
    ...{ [SIDEBAR_DATA_ATTRIBUTES.ITEM]: "" },
    ...{ [SIDEBAR_DATA_ATTRIBUTES.FOCUSED]: isFocused },
    ...{ [SIDEBAR_DATA_ATTRIBUTES.HOVERED]: isHovered },
    ...{ [SIDEBAR_DATA_ATTRIBUTES.DISABLED]: disabled },
    "data-item-id": itemId,
    ...props,
  }

  // Si es render prop, delegar renderizado completo
  if (isRenderProp) {
    return <Component {...baseProps}>{(children as SidebarItemRenderProp)(itemState)}</Component>
  }

  // Si hay children custom (no render prop), renderizarlos (excluyendo SidebarSubContent)
  if (otherChildren) {
    return (
      <Component href={href} {...linkProps} {...baseProps}>
        {otherChildren}
      </Component>
    )
  }

  // Renderizado por defecto con icono y label
  return (
    <Component href={href} {...linkProps} {...baseProps}>
      {icon && (
        <span
          className="sidebar-item-icon-wrapper relative z-[15] inline-flex items-center justify-center p-2 rounded-xl transition-all duration-300"
          data-hovered={isHovered}
          data-focused={isFocused}
        >
          {icon}
        </span>
      )}
      {label && <span className="sidebar-item-label sr-only">{label}</span>}
    </Component>
  )
})
